import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import {
  Box, Typography, TextField, Button,
  InputAdornment, IconButton, FormControlLabel,
  Checkbox, Alert, CircularProgress,
  Dialog, DialogContent
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  FitnessCenter as GymIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Autorenew as RenewIcon,
  Close as CloseIcon,
  ShieldOutlined as ShieldIcon
} from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { login } from '../../redux/slices/authSlice';

const validationSchema = Yup.object({
  email: Yup.string().email('Invalid email address').required('Email is required'),
  password: Yup.string().required('Password is required')
});

const FEATURES = [
  'Member Attendance Tracking',
  'Smart Billing & Payments',
  'AI Fitness Coach',
  'Trainer Management',
  'Live Chat Support',
  'Product Catalog'
];

// Brand accent — used consistently across both themes
const ACCENT = '#6366f1';
const ACCENT_2 = '#8b5cf6';
const BRAND_GRADIENT = 'linear-gradient(135deg, #6366f1 0%, #7c3aed 55%, #a855f7 100%)';

// ── Theme-aware design tokens ──────────────────────────────────
const buildTokens = (dark) => ({
  pageBg: dark
    ? 'radial-gradient(1200px 600px at 100% -10%, #1e1b4b 0%, transparent 55%), radial-gradient(900px 500px at -10% 110%, #0f172a 0%, transparent 60%), #070b16'
    : 'radial-gradient(1200px 600px at 100% -10%, #eef2ff 0%, transparent 55%), radial-gradient(900px 500px at -10% 110%, #faf5ff 0%, transparent 60%), #f6f7fb',
  cardBg: dark ? 'rgba(17,24,39,0.72)' : 'rgba(255,255,255,0.86)',
  cardBorder: dark ? 'rgba(148,163,184,0.14)' : 'rgba(15,23,42,0.08)',
  cardShadow: dark
    ? '0 30px 80px -30px rgba(0,0,0,0.85), 0 0 0 1px rgba(148,163,184,0.04)'
    : '0 30px 70px -32px rgba(79,70,229,0.28), 0 2px 10px rgba(15,23,42,0.05)',
  heading: dark ? '#f8fafc' : '#0f172a',
  text: dark ? '#cbd5e1' : '#475569',
  muted: dark ? '#94a3b8' : '#64748b',
  label: dark ? '#94a3b8' : '#64748b',
  inputBg: dark ? 'rgba(2,6,23,0.55)' : '#ffffff',
  inputBorder: dark ? 'rgba(148,163,184,0.22)' : 'rgba(15,23,42,0.14)',
  inputText: dark ? '#f1f5f9' : '#0f172a',
  placeholder: dark ? '#64748b' : '#94a3b8',
  divider: dark ? 'rgba(148,163,184,0.12)' : 'rgba(15,23,42,0.08)'
});

const buildInputSx = (t) => ({
  '& .MuiOutlinedInput-root': {
    bgcolor: t.inputBg,
    borderRadius: '14px',
    color: t.inputText,
    fontSize: 15,
    fontWeight: 500,
    transition: 'box-shadow .25s ease, border-color .25s ease, background-color .25s ease',
    '& fieldset': {
      borderColor: t.inputBorder,
      borderWidth: 1.5,
      transition: 'border-color .2s ease'
    },
    '&:hover fieldset': { borderColor: ACCENT },
    '&.Mui-focused': {
      boxShadow: `0 0 0 4px ${ACCENT}26`,
      '& fieldset': { borderColor: ACCENT, borderWidth: 2 }
    },
    '&.Mui-error fieldset': { borderColor: '#ef4444' }
  },
  '& .MuiInputBase-input': {
    color: t.inputText,
    padding: '15px 16px',
    fontWeight: 500,
    '&::placeholder': { color: t.placeholder, opacity: 1, fontWeight: 400 },
    '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus': {
      WebkitBoxShadow: `0 0 0 1000px ${t.inputBg} inset`,
      WebkitTextFillColor: t.inputText,
      caretColor: ACCENT,
      borderRadius: '14px',
      transition: 'background-color 9999s ease-out'
    }
  },
  '& .MuiFormHelperText-root': { color: '#ef4444', mt: 0.75, ml: 0.25, fontSize: 12.5, fontWeight: 500 }
});

// ── Gym Blocked (subscription expired) full screen ────────────
const GymBlockedScreen = ({ message, reason, onBack }) => (
  <Box sx={{
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'radial-gradient(900px 500px at 50% -10%, #3f1d1d 0%, transparent 55%), #0a0a0f',
    position: 'relative', overflow: 'hidden', p: 2
  }}>
    <Box sx={{ position: 'absolute', width: 500, height: 500, borderRadius: '50%', bgcolor: '#7f1d1d', top: -200, left: -100, filter: 'blur(120px)', opacity: 0.22 }} />
    <Box sx={{ position: 'absolute', width: 400, height: 400, borderRadius: '50%', bgcolor: '#991b1b', bottom: -150, right: -100, filter: 'blur(120px)', opacity: 0.18 }} />

    <Box sx={{
      position: 'relative', zIndex: 1, maxWidth: 460, width: '100%',
      background: 'rgba(17,10,10,0.9)', backdropFilter: 'blur(24px)',
      border: '1px solid rgba(239,68,68,0.22)', borderRadius: '24px',
      boxShadow: '0 40px 100px rgba(0,0,0,0.6)', overflow: 'hidden', textAlign: 'center'
    }}>
      <Box sx={{ height: 4, background: 'linear-gradient(90deg, #ef4444, #dc2626)' }} />
      <Box sx={{ p: { xs: 4, sm: 5 } }}>
        <Box sx={{
          width: 80, height: 80, borderRadius: '50%', mx: 'auto', mb: 3,
          background: 'rgba(239,68,68,0.1)', border: '2px solid rgba(239,68,68,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: 'pulse 2s ease-in-out infinite',
          '@keyframes pulse': {
            '0%,100%': { boxShadow: '0 0 0 0 rgba(239,68,68,0.3)' },
            '50%': { boxShadow: '0 0 0 14px rgba(239,68,68,0)' }
          }
        }}>
          <WarningIcon sx={{ fontSize: 40, color: '#f87171' }} />
        </Box>

        <Typography fontWeight={800} sx={{ color: '#fca5a5', fontSize: 24, mb: 1.5 }}>
          {reason === 'subscription_expired' ? 'Subscription Expired' : 'Account Suspended'}
        </Typography>

        <Typography sx={{ color: '#94a3b8', fontSize: 14, lineHeight: 1.8, mb: 4 }}>
          {message}
        </Typography>

        <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.15)', mb: 4 }}>
          <Typography sx={{ color: '#f87171', fontSize: 13, fontWeight: 600 }}>
            To restore access, contact your Super Administrator to renew the gym subscription.
          </Typography>
        </Box>

        <Button
          fullWidth
          onClick={onBack}
          sx={{
            py: 1.5, borderRadius: '12px', fontWeight: 700, fontSize: 14, textTransform: 'none',
            background: 'linear-gradient(135deg,#1e293b,#334155)',
            color: '#cbd5e1', border: '1px solid rgba(255,255,255,0.08)',
            '&:hover': { background: 'linear-gradient(135deg,#334155,#475569)', color: '#fff' }
          }}
        >
          Back to Login
        </Button>
      </Box>
    </Box>
  </Box>
);

// ── Expiry notice dialog ───────────────────────────────────────
const ExpiryNotice = ({ daysLeft, onContinue, onRenew }) => (
  <Dialog open maxWidth="xs" fullWidth
    PaperProps={{
      sx: {
        background: 'linear-gradient(145deg, #121528 0%, #1b1535 100%)',
        border: '1px solid rgba(251,191,36,0.25)',
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: '0 40px 100px rgba(0,0,0,0.7)'
      }
    }}
  >
    <Box sx={{ height: 4, background: 'linear-gradient(90deg, #f59e0b, #ef4444)' }} />

    <DialogContent sx={{ p: 4, textAlign: 'center' }}>
      <Box sx={{
        width: 72, height: 72, borderRadius: '50%', mx: 'auto', mb: 2.5,
        background: 'rgba(251,191,36,0.12)',
        border: '2px solid rgba(251,191,36,0.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'pulse 2s ease-in-out infinite',
        '@keyframes pulse': {
          '0%,100%': { boxShadow: '0 0 0 0 rgba(251,191,36,0.3)' },
          '50%': { boxShadow: '0 0 0 12px rgba(251,191,36,0)' }
        }
      }}>
        <WarningIcon sx={{ fontSize: 36, color: '#fbbf24' }} />
      </Box>

      <Typography fontWeight={800} sx={{ color: '#f1f5f9', fontSize: 22, mb: 1 }}>
        Membership Expiring Soon!
      </Typography>

      <Typography sx={{ color: '#94a3b8', fontSize: 14, mb: 1, lineHeight: 1.7 }}>
        Your membership expires in
      </Typography>

      <Box sx={{
        display: 'inline-flex', alignItems: 'baseline', gap: 0.75,
        bgcolor: 'rgba(251,191,36,0.08)', borderRadius: '12px',
        px: 3, py: 1.5, mb: 3,
        border: '1px solid rgba(251,191,36,0.2)'
      }}>
        <Typography fontWeight={900} sx={{
          fontSize: 48, color: daysLeft <= 3 ? '#ef4444' : '#fbbf24',
          lineHeight: 1
        }}>
          {daysLeft}
        </Typography>
        <Typography fontWeight={700} sx={{ color: '#94a3b8', fontSize: 16 }}>
          {daysLeft === 1 ? 'day' : 'days'}
        </Typography>
      </Box>

      <Typography sx={{ color: '#64748b', fontSize: 13, mb: 3.5, lineHeight: 1.6 }}>
        Renew now to keep uninterrupted access to all gym services, classes, and your personal trainer.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Button
          fullWidth
          startIcon={<RenewIcon />}
          onClick={onRenew}
          sx={{
            py: 1.4, borderRadius: '12px', fontWeight: 700, fontSize: 14, textTransform: 'none',
            background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
            color: '#fff',
            boxShadow: '0 8px 24px rgba(245,158,11,0.35)',
            '&:hover': {
              background: 'linear-gradient(135deg, #d97706, #dc2626)',
              boxShadow: '0 12px 32px rgba(245,158,11,0.45)',
              transform: 'translateY(-1px)'
            }
          }}
        >
          Renew Membership Now
        </Button>
        <Button
          fullWidth
          startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
          onClick={onContinue}
          sx={{
            py: 1.2, borderRadius: '12px', fontWeight: 600, fontSize: 13, textTransform: 'none',
            color: '#94a3b8', border: '1px solid rgba(255,255,255,0.08)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.04)', color: '#e2e8f0' }
          }}
        >
          Continue to Dashboard
        </Button>
      </Box>
    </DialogContent>
  </Dialog>
);

// ── Main Login component ───────────────────────────────────────
const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const t = buildTokens(dark);
  const inputSx = buildInputSx(t);

  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [expiryInfo, setExpiryInfo] = useState(null);
  const [gymBlocked, setGymBlocked] = useState(null); // { message, reason }

  useEffect(() => { const id = setTimeout(() => setMounted(true), 60); return () => clearTimeout(id); }, []);

  // Shown once after being logged out by the server (e.g. membership expired)
  useEffect(() => {
    try {
      const notice = sessionStorage.getItem('loginNotice');
      if (notice) {
        setErrorMessage(notice);
        sessionStorage.removeItem('loginNotice');
      }
    } catch (e) {}
  }, []);

  const handleSubmit = async (values, { setSubmitting }) => {
    setErrorMessage('');
    setLoading(true);
    try {
      const result = await dispatch(login(values));

      // Check if user is super admin
      const role = result.user?.role;
      if (role === 'superadmin') {
        // Store super admin credentials
        localStorage.setItem('superAdminToken', result.token);
        localStorage.setItem('superAdminUser', JSON.stringify(result.user));
        // Redirect to super admin dashboard
        setTimeout(() => navigate('/superadmin/dashboard'), 500);
        return;
      }

      if (result.user.isFirstLogin) {
        navigate('/change-password', { state: { isFirstLogin: true } });
        return;
      }

      // Check membership expiry for member role
      const endDate = result.user?.membershipEndDate || result.user?.membership?.endDate;
      if (role === 'member' && endDate) {
        const days = Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24));
        if (days >= 0 && days <= 7) {
          setExpiryInfo({ daysLeft: days });
          setLoading(false);
          return;
        }
      }

      // Regular admin or member - go to main dashboard
      setTimeout(() => navigate('/'), 500);
    } catch (error) {
      const data = error.response?.data;
      if (data?.reason === 'membership_expired') {
        // Member's own membership ended: login is locked until the gym renews it
        setErrorMessage(data.message || 'Your membership has expired. Please contact the gym to renew.');
      } else if (data?.gymBlocked) {
        setGymBlocked({ message: data.message, reason: data.reason });
      } else {
        setErrorMessage(data?.message || 'Invalid email or password. Please try again.');
      }
      setLoading(false);
      setSubmitting(false);
    }
  };

  // Show full-screen blocked UI
  if (gymBlocked) {
    return <GymBlockedScreen message={gymBlocked.message} reason={gymBlocked.reason} onBack={() => setGymBlocked(null)} />;
  }

  const brandLockup = (size = 'lg') => {
    const small = size === 'sm';
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: small ? 44 : 52, height: small ? 44 : 52, borderRadius: small ? '13px' : '15px',
          background: BRAND_GRADIENT,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 12px 28px rgba(99,102,241,0.45)'
        }}>
          <GymIcon sx={{ color: '#fff', fontSize: small ? 24 : 28 }} />
        </Box>
        <Box>
          <Typography fontWeight={800} sx={{ color: small ? t.heading : '#fff', fontSize: small ? 16 : 19, lineHeight: 1.1, letterSpacing: '-0.3px' }}>
            Gym Manager
          </Typography>
          <Typography sx={{ color: small ? t.muted : 'rgba(255,255,255,0.55)', fontSize: small ? 9.5 : 10.5, letterSpacing: 2, fontWeight: 600 }}>
            BY FINTECH
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', background: t.pageBg,
      overflow: 'hidden', position: 'relative',
      '@keyframes floatOrb': {
        '0%,100%': { transform: 'translateY(0) scale(1)' },
        '50%': { transform: 'translateY(-32px) scale(1.06)' }
      },
      '@keyframes riseIn': {
        from: { opacity: 0, transform: 'translateY(22px)' },
        to:   { opacity: 1, transform: 'translateY(0)' }
      },
      '@keyframes fadeInLeft': {
        from: { opacity: 0, transform: 'translateX(-28px)' },
        to:   { opacity: 1, transform: 'translateX(0)' }
      }
    }}>
      {/* Soft ambient orbs — subtle, restrained */}
      {[
        { w: 620, h: 620, c: ACCENT, t: -260, l: -200, d: '0s', o: dark ? 0.22 : 0.14, blur: 130 },
        { w: 480, h: 480, c: ACCENT_2, b: -220, l: '30%', d: '3s', o: dark ? 0.18 : 0.12, blur: 120 },
        { w: 360, h: 360, c: '#a855f7', t: '20%', r: '-6%', d: '6s', o: dark ? 0.16 : 0.1, blur: 110 }
      ].map((o, i) => (
        <Box key={i} sx={{
          position: 'absolute', borderRadius: '50%',
          width: o.w, height: o.h, background: o.c,
          top: o.t, bottom: o.b, left: o.l, right: o.r,
          filter: `blur(${o.blur}px)`, opacity: o.o,
          animation: `floatOrb ${16 + i * 3}s ease-in-out infinite`,
          animationDelay: o.d, zIndex: 0
        }} />
      ))}

      {/* ── LEFT PANEL (brand / marketing) ───────────────────── */}
      <Box sx={{
        flex: '0 0 48%',
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column', justifyContent: 'center',
        px: { lg: 7, xl: 10 }, py: 6, m: { lg: 2.5 },
        position: 'relative', zIndex: 1,
        borderRadius: '28px', overflow: 'hidden',
        background: 'linear-gradient(155deg, #4f46e5 0%, #6d28d9 48%, #1e1b4b 100%)',
        boxShadow: '0 40px 90px -40px rgba(79,70,229,0.6)',
        opacity: mounted ? 1 : 0,
        animation: mounted ? 'fadeInLeft 0.7s ease forwards' : 'none'
      }}>
        {/* texture overlay */}
        <Box sx={{
          position: 'absolute', inset: 0, opacity: 0.5, pointerEvents: 'none',
          background: 'radial-gradient(600px 300px at 90% 0%, rgba(255,255,255,0.14), transparent 60%), radial-gradient(500px 300px at 0% 100%, rgba(168,85,247,0.35), transparent 55%)'
        }} />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          {brandLockup('lg')}

          <Typography fontWeight={900} sx={{
            color: '#fff', lineHeight: 1.08, mt: 7, mb: 2.5,
            fontSize: { lg: '2.7rem', xl: '3.3rem' }, letterSpacing: '-1.5px'
          }}>
            Power Your{' '}
            <Box component="span" sx={{
              background: 'linear-gradient(135deg, #c4b5fd, #f0abfc)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
            }}>
              Fitness
            </Box>
            <br />Business
          </Typography>

          <Typography sx={{
            color: 'rgba(255,255,255,0.72)', mb: 5, lineHeight: 1.85,
            fontSize: 15.5, maxWidth: 420
          }}>
            The all-in-one platform to manage members, trainers, payments, and workouts — designed for modern gyms.
          </Typography>

          {/* Feature checklist */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
            {FEATURES.map(f => (
              <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                  width: 24, height: 24, borderRadius: '8px',
                  background: 'rgba(255,255,255,0.14)', backdropFilter: 'blur(4px)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                }}>
                  <CheckIcon sx={{ fontSize: 15, color: '#e9d5ff' }} />
                </Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.88)', fontSize: 14.5, fontWeight: 500 }}>
                  {f}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ mt: 7, pt: 3, borderTop: '1px solid rgba(255,255,255,0.14)' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 12.5 }}>
              © {new Date().getFullYear()} Fintech. All Rights Reserved.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── RIGHT PANEL (form) ───────────────────────────────── */}
      <Box sx={{
        flex: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        px: { xs: 2.5, sm: 4 }, py: { xs: 4, sm: 6 },
        position: 'relative', zIndex: 1,
        opacity: mounted ? 1 : 0,
        animation: mounted ? 'riseIn 0.7s ease forwards' : 'none'
      }}>
        <Box sx={{
          width: '100%', maxWidth: 440,
          background: t.cardBg,
          backdropFilter: 'blur(28px) saturate(160%)',
          WebkitBackdropFilter: 'blur(28px) saturate(160%)',
          border: `1px solid ${t.cardBorder}`,
          borderRadius: '26px',
          p: { xs: 3.5, sm: 5 },
          boxShadow: t.cardShadow,
          position: 'relative', overflow: 'hidden',
          '&::before': {
            content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
            background: BRAND_GRADIENT
          }
        }}>
          {/* Mobile brand */}
          <Box sx={{ display: { xs: 'flex', lg: 'none' }, mb: 4 }}>
            {brandLockup('sm')}
          </Box>

          {/* Heading */}
          <Box sx={{ mb: 4 }}>
            <Typography fontWeight={900} sx={{
              fontSize: { xs: 27, sm: 31 }, mb: 1, letterSpacing: '-0.8px', color: t.heading
            }}>
              Welcome back
            </Typography>
            <Typography sx={{ color: t.muted, fontSize: 15, fontWeight: 500, lineHeight: 1.6 }}>
              Sign in to access your{' '}
              <Box component="span" sx={{
                background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT_2})`,
                WebkitBackgroundClip: 'text', backgroundClip: 'text',
                WebkitTextFillColor: 'transparent', fontWeight: 700
              }}>
                gym management dashboard
              </Box>
            </Typography>
          </Box>

          {errorMessage && (
            <Alert severity="error" data-testid="login-error" sx={{
              mb: 3, borderRadius: '12px', alignItems: 'center',
              bgcolor: dark ? 'rgba(239,68,68,0.12)' : 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.28)',
              color: dark ? '#fca5a5' : '#b91c1c',
              '& .MuiAlert-icon': { color: '#ef4444' }
            }}>
              {errorMessage}
            </Alert>
          )}

          <Formik
            initialValues={{ email: '', password: '', rememberMe: false }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, values, handleChange, handleBlur }) => (
              <Form>
                {/* Email */}
                <Box sx={{ mb: 2.5 }}>
                  <Typography sx={{ color: t.label, fontSize: 11.5, fontWeight: 700, letterSpacing: 0.8, mb: 1 }}>
                    EMAIL ADDRESS
                  </Typography>
                  <TextField
                    fullWidth name="email" type="email"
                    placeholder="you@example.com"
                    value={values.email}
                    onChange={handleChange} onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: ACCENT, fontSize: 19 }} />
                        </InputAdornment>
                      )
                    }}
                    sx={inputSx}
                  />
                </Box>

                {/* Password */}
                <Box sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography sx={{ color: t.label, fontSize: 11.5, fontWeight: 700, letterSpacing: 0.8 }}>
                      PASSWORD
                    </Typography>
                    <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
                      <Typography sx={{ color: ACCENT, fontSize: 12.5, fontWeight: 600, '&:hover': { color: ACCENT_2 }, transition: 'color 0.2s' }}>
                        Forgot password?
                      </Typography>
                    </Link>
                  </Box>
                  <TextField
                    fullWidth name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={values.password}
                    onChange={handleChange} onBlur={handleBlur}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: ACCENT, fontSize: 19 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(v => !v)} edge="end" size="small"
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            sx={{ color: t.muted, '&:hover': { color: ACCENT } }}>
                            {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={inputSx}
                  />
                </Box>

                {/* Remember me */}
                <FormControlLabel
                  control={
                    <Checkbox
                      name="rememberMe" checked={values.rememberMe} onChange={handleChange}
                      size="small"
                      sx={{ color: t.inputBorder, '&.Mui-checked': { color: ACCENT }, p: '6px' }}
                    />
                  }
                  label={<Typography sx={{ color: t.text, fontSize: 13.5 }}>Keep me signed in</Typography>}
                  sx={{ mb: 3, ml: 0 }}
                />

                {/* Sign In Button */}
                <Button
                  type="submit" fullWidth disabled={loading}
                  endIcon={!loading && <ArrowForwardIcon sx={{ fontSize: 19 }} />}
                  sx={{
                    py: 1.7,
                    borderRadius: '14px',
                    fontWeight: 700,
                    fontSize: 15.5,
                    letterSpacing: 0.3,
                    textTransform: 'none',
                    color: '#fff',
                    background: BRAND_GRADIENT,
                    backgroundSize: '160% 100%',
                    backgroundPosition: 'left center',
                    boxShadow: '0 14px 30px -8px rgba(99,102,241,0.6)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""', position: 'absolute', top: 0, left: '-130%',
                      width: '60%', height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)',
                      transform: 'skewX(-20deg)', transition: 'left 0.6s ease'
                    },
                    '&:hover:not(:disabled)': {
                      backgroundPosition: 'right center',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 20px 40px -8px rgba(99,102,241,0.7)',
                      '&::before': { left: '140%' }
                    },
                    '&:active:not(:disabled)': { transform: 'translateY(0)' },
                    '&.Mui-disabled': {
                      background: dark ? 'rgba(99,102,241,0.25)' : 'rgba(99,102,241,0.4)',
                      color: 'rgba(255,255,255,0.7)',
                      boxShadow: 'none'
                    }
                  }}
                >
                  {loading
                    ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CircularProgress size={16} sx={{ color: '#fff' }} /> Signing in...
                      </Box>
                    : 'Sign In'}
                </Button>

                {/* Trust line */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, mt: 2.5 }}>
                  <ShieldIcon sx={{ fontSize: 15, color: t.muted }} />
                  <Typography sx={{ color: t.muted, fontSize: 12 }}>
                    Secure, encrypted sign-in
                  </Typography>
                </Box>
              </Form>
            )}
          </Formik>

          <Typography sx={{
            display: { xs: 'block', lg: 'none' },
            textAlign: 'center', mt: 3,
            color: t.muted, fontSize: 11.5
          }}>
            © {new Date().getFullYear()} Fintech. All Rights Reserved.
          </Typography>
        </Box>
      </Box>

      {/* ── Membership Expiry Notice ────────────────────────── */}
      {expiryInfo && (
        <ExpiryNotice
          daysLeft={expiryInfo.daysLeft}
          onContinue={() => { setExpiryInfo(null); navigate('/'); }}
          onRenew={() => { setExpiryInfo(null); navigate('/member/renewal'); }}
        />
      )}
    </Box>
  );
};

export default Login;
